import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  DRIVERS, SPRINTS, S,
  CELL_W, TRACK_H, TRACK_PAD, LANE_PAD,
  VALUE_BADGE, scoreStyle, scoreLabel,
  assignTracks, tracksUsed, laneHeight, getTracksUsedInSprint,
  getHalf, getMonthLabel, formatDate,
} from "./constants";
import { DriverTag, ConfluenceIcon, JiraStatusBadge } from "./components";

const STICKY_LABEL = { position: "sticky", left: 0, zIndex: 21 };

// ─── NotesDot ─────────────────────────────────────────────────────────────────
function NotesDot({ notes }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const show = useCallback(() => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ x: r.left + r.width / 2, y: r.top });
  }, []);
  const hide = useCallback(() => setPos(null), []);

  return (
    <span ref={ref} onMouseEnter={show} onMouseLeave={hide}
      onFocus={show} onBlur={hide}
      tabIndex={0} role="button" aria-label={`Notes: ${notes}`}
      style={{ width:6, height:6, borderRadius:"50%", background:"var(--accent)", flexShrink:0,
        display:"inline-block", cursor:"default", opacity:0.7 }}
    >
      {pos && createPortal(
        <div style={{
          position:"fixed", left: pos.x, top: pos.y - 6,
          transform:"translate(-50%, -100%)",
          background:"var(--bg-card)", border:"1px solid var(--border)",
          borderRadius:7, padding:"6px 10px",
          fontSize:11, color:"var(--text-primary)", maxWidth:240,
          boxShadow:"0 6px 20px rgba(0,0,0,0.16)", zIndex:9999,
          pointerEvents:"none", whiteSpace:"pre-wrap", lineHeight:1.5,
        }}>
          <span style={{ fontSize:9, fontWeight:800, color:"var(--text-muted)", textTransform:"uppercase",
            letterSpacing:"0.06em", display:"block", marginBottom:3 }}>Notes</span>
          {notes}
        </div>,
        document.body
      )}
    </span>
  );
}

// ─── CompletedSection ─────────────────────────────────────────────────────────
function CompletedSection({ completed, visibleSprints, onEdit, onRestore, lanes = [], scoringMethod = "score" }) {
  const [collapsed, setCollapsed] = useState(false);
  const visList  = SPRINTS.slice(visibleSprints[0], visibleSprints[1] + 1);
  const trackMap = assignTracks(completed);

  if (completed.length === 0) return null;

  const laneGroups = lanes.map((lane) => ({
    lane,
    items: completed.filter((p) => p.lane === lane.id && p.sprint !== undefined),
  })).filter((g) => g.items.length > 0);

  return (
    <div style={{ borderTop:"2px solid var(--completed-border)", flexShrink:0 }}>
      <div style={{ display:"flex", alignItems:"center", padding:"6px 12px", background:"var(--completed-bg)",
        borderBottom: collapsed ? "none" : "1px solid var(--completed-border)", cursor:"pointer" }}
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed} aria-label="Toggle completed projects">
        <div style={{ width:140, flexShrink:0, display:"flex", alignItems:"center", gap:6, ...STICKY_LABEL, background:"var(--completed-bg)" }}>
          <span style={{ fontSize:13, color:"var(--color-info,#0369A1)" }}>{collapsed ? "▶" : "▼"}</span>
          <span style={{ fontSize:11, fontWeight:800, color:"var(--color-info,#0369A1)", textTransform:"uppercase", letterSpacing:"0.06em" }}>
            Completed
          </span>
          <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:10,
            background:"var(--color-info,#0369A1)", color:"#fff" }}>{completed.length}</span>
        </div>
        <div style={{ fontSize:10, color:"var(--text-muted)" }}>
          {collapsed ? "Click to expand" : "Click to collapse"}
        </div>
      </div>

      {!collapsed && laneGroups.map(({ lane, items }) => {
        const numTracks = tracksUsed(items, lane.id, trackMap);
        const laneH     = laneHeight(numTracks);

        return (
          <div key={lane.id} style={{ display:"flex", alignItems:"stretch",
            borderBottom:"1px solid var(--completed-border)", height:laneH, flexShrink:0, background:"var(--completed-bg)" }}>
            <div style={{ ...S.laneLabel, ...STICKY_LABEL, borderLeft:`4px solid ${lane.color}88`, background:"var(--completed-bg)",
              flexDirection:"column", alignItems:"flex-start", justifyContent:"center", gap:2 }}>
              <span style={{ fontSize:10, fontWeight:800, color:lane.color+"99", letterSpacing:"0.04em", textTransform:"uppercase" }}>
                {lane.label}
              </span>
            </div>

            <div style={{ display:"flex", flex:1, position:"relative" }}>
              {visList.map((s,i) => (
                <div key={s.id} style={{
                  position:"absolute", left:i*CELL_W, top:0, width:CELL_W, height:laneH,
                  background: i%2===0 ? "rgba(0,0,0,0.01)" : "transparent",
                  borderRight:"1px solid #E0F2FE", pointerEvents:"none",
                }} />
              ))}

              {items.map((p) => {
                const visStart = p.sprint - visibleSprints[0];
                const visEnd   = visStart + p.sprints;
                if (visEnd <= 0 || visStart >= visList.length) return null;
                const cStart  = Math.max(0, visStart);
                const cEnd    = Math.min(visList.length, visEnd);
                const blockW  = (cEnd - cStart) * CELL_W - 20;
                const track   = trackMap[p.id] ?? 0;
                const topPos  = LANE_PAD + track * TRACK_H + TRACK_PAD;
                const blockH  = 65;
                const drv     = DRIVERS[p.driver] || DRIVERS.core;
                const rStyle  = scoreStyle(p.score, scoringMethod);

                return (
                  <div key={p.id}
                    onClick={() => onEdit?.(p)}
                    style={{
                      position:"absolute", zIndex:10, cursor: onEdit ? "pointer" : "default",
                      left: cStart * CELL_W, top: topPos,
                      width: blockW, height: blockH,
                      borderRadius:5, background:"var(--completed-bg)",
                      border:"1px solid var(--completed-border)",
                      borderTop:`3px solid ${lane.color}88`,
                      boxShadow:"0 1px 3px rgba(0,0,0,0.06)",
                      padding:"4px 7px",
                      display:"flex", flexDirection:"column", justifyContent:"space-between",
                      overflow:"hidden", opacity:0.85,
                    }}
                    title={`${p.name} — Completed. Click to edit.`}
                  >
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:3 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:"var(--text-secondary)", lineHeight:1.3, flex:1,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                        textDecoration:"none" }}>
                        ✓ {p.name}
                      </span>
                      <button onClick={(e) => { e.stopPropagation(); onRestore?.(p); }}
                        title="Restore to scheduled" aria-label="Restore to scheduled"
                        style={{ background:"transparent", border:"none", color:"var(--text-muted)", cursor:"pointer",
                          fontSize:11, lineHeight:1, padding:0, fontFamily:"inherit", flexShrink:0 }}>↺</button>
                    </div>
                    <div style={{ display:"flex", gap:2, alignItems:"center", overflow:"hidden" }}>
                      <DriverTag driver={p.driver} compact />
                      <span style={{ ...S.pillMuted }}>{drv.short}</span>
                      <span style={{ ...S.pill, background:rStyle.bg, color:rStyle.text, border:`1px solid ${rStyle.border}` }}>{scoreLabel(p.score, scoringMethod)}</span>
                      <span style={S.pillMuted}>{p.value}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── BacklogPanel ─────────────────────────────────────────────────────────────
function BacklogPanel({ backlog, dragging, setDragging, onDropBacklog, onReorderBacklog, onEdit, onRemove, open, onToggle, canEdit, lanes = [], scoringMethod = "score" }) {
  const [dropIndex, setDropIndex] = useState(null);
  const [tabHover,  setTabHover]  = useState(false);
  const insidePanelRef = useRef(false);
  const enterCount     = useRef(0);
  const [insidePanel, setInsidePanel] = useState(false);

  const isBacklogDrag = dragging?.source === "backlog";
  const isReordering  = isBacklogDrag && insidePanel;
  const isGridDrag    = dragging?.source === "grid";

  const handleDragStart = (e, p, idx) => {
    e.dataTransfer.effectAllowed = "move";
    insidePanelRef.current = true;
    setInsidePanel(true);
    enterCount.current = 0;
    setDragging({ project: p, source: "backlog", fromIndex: idx });
  };

  const handlePanelDragEnter = (e) => {
    e.preventDefault();
    enterCount.current += 1;
    insidePanelRef.current = true;
    setInsidePanel(true);
  };

  const handlePanelDragLeave = () => {
    enterCount.current -= 1;
    if (enterCount.current <= 0) {
      enterCount.current = 0;
      insidePanelRef.current = false;
      setInsidePanel(false);
      setDropIndex(null);
    }
  };

  const handlePanelDragOver = (e) => {
    e.preventDefault();
    if (isBacklogDrag && insidePanelRef.current) {
      setDropIndex((d) => (d ?? backlog.length));
    }
  };

  const handleCardDragOver = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    if (isReordering) setDropIndex(idx);
  };

  const handlePanelDrop = (e) => {
    e.preventDefault();
    if (!onDropBacklog) return;
    if (isReordering && dropIndex !== null) {
      onReorderBacklog?.(dragging.fromIndex, dropIndex);
    } else {
      onDropBacklog(e);
    }
    setDropIndex(null);
    insidePanelRef.current = false;
    setInsidePanel(false);
    enterCount.current = 0;
  };

  const handleCardDrop = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    if (isBacklogDrag && onReorderBacklog) {
      onReorderBacklog(dragging.fromIndex, idx);
      setDropIndex(null);
      insidePanelRef.current = false;
      setInsidePanel(false);
      enterCount.current = 0;
    }
  };

  const handleTabDragOver = (e) => { e.preventDefault(); };
  const handleTabDrop     = (e) => { onDropBacklog?.(e); };

  const DropLine = () => (
    <div style={{ height: 2, borderRadius: 2, background: "#3B82F6", margin: "2px 0", boxShadow: "0 0 0 2px #BFDBFE", pointerEvents: "none" }} />
  );

  return (
    <div style={{ display:"flex", flexShrink:0, position:"relative" }}>
      {/* Toggle tab */}
      <div
        onClick={onToggle}
        onMouseEnter={() => setTabHover(true)}
        onMouseLeave={() => setTabHover(false)}
        onDragOver={handleTabDragOver}
        onDrop={handleTabDrop}
        title={open ? "Collapse backlog" : "Expand backlog"}
        aria-label={open ? "Collapse backlog" : "Expand backlog"}
        aria-expanded={open}
        role="button"
        style={{
          width: 22, cursor: "pointer", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 6,
          borderLeft: "1px solid #E2E8F0",
          background: isGridDrag ? "var(--sprint-next-bg)" : tabHover ? "var(--bg-hover)" : "var(--bg-subtle)",
          transition: "background 0.15s", userSelect: "none", flexShrink: 0,
        }}
      >
        <span style={{
          fontSize: 10, color: isGridDrag ? "#1D4ED8" : "var(--text-muted)",
          transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.2s",
          display: "block", lineHeight: 1,
        }}>›</span>
        <span style={{
          fontSize: 9, fontWeight: 800, color: isGridDrag ? "#1D4ED8" : "var(--text-muted)",
          textTransform: "uppercase", letterSpacing: "0.12em",
          writingMode: "vertical-rl", textOrientation: "mixed", transform: "rotate(180deg)",
        }}>
          Backlog{backlog.length > 0 ? ` (${backlog.length})` : ""}
        </span>
        {isGridDrag && !open && (
          <span style={{ fontSize: 14, lineHeight: 1 }}>⬅</span>
        )}
      </div>

      {open && (
        <div
          style={{ ...S.backlogPanel }}
          onDragEnter={handlePanelDragEnter}
          onDragLeave={handlePanelDragLeave}
          onDragOver={handlePanelDragOver}
          onDrop={handlePanelDrop}
        >
          <div style={S.backlogTitle}>Backlog</div>
          <div style={{ fontSize:10, color:"var(--text-muted)", marginBottom:8, lineHeight:1.5 }}>
            Drag onto roadmap to schedule.<br/>
            <span style={{ color: isReordering ? "#1D4ED8" : "var(--text-muted)", fontWeight: isReordering ? 800 : 600 }}>
              {isReordering ? "↕ Reordering… drop to insert" : "☰ Drag cards to reprioritise."}
            </span>
          </div>

          {backlog.length === 0 && (
            <div style={{ color:"var(--text-muted)", fontSize:11, textAlign:"center", padding:"20px 0", border:"2px dashed var(--border)", borderRadius:8 }}>
              Drop projects here
            </div>
          )}

          {isReordering && dropIndex === 0 && <DropLine />}

          {backlog.map((p, idx) => {
            const lane   = lanes.find((l) => l.id === p.lane);
            const drv    = DRIVERS[p.driver] || DRIVERS.core;
            const isDraggingThis = isBacklogDrag && dragging?.project?.id === p.id;

            return (
              <div key={p.id}>
                <div draggable={canEdit}
                  onDragStart={canEdit ? (e) => handleDragStart(e, p, idx) : undefined}
                  onDragOver={canEdit ? (e) => handleCardDragOver(e, idx + 1) : undefined}
                  onDrop={canEdit ? (e) => handleCardDrop(e, idx + 1) : undefined}
                  onClick={() => onEdit?.(p)}
                  style={{
                    ...S.backlogCard, borderTop: `3px solid ${drv.color}`,
                    opacity: isDraggingThis ? 0.4 : 1, transition: "opacity 0.15s", cursor: canEdit ? "grab" : "default",
                  }} title={canEdit ? "Click to edit" : "Login to edit"}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:2 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{ fontSize:9, fontWeight:800, minWidth:16, height:16, borderRadius:"50%", background:"var(--bg-hover)", color:"var(--text-secondary)",
                        display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{idx + 1}</span>
                      <span style={{ fontSize:10, color:"var(--text-muted)", lineHeight:1, cursor:"grab", letterSpacing:"-1px" }} title="Drag to reprioritise">⠿</span>
                      <div style={{ fontWeight:700, fontSize:11, color:"var(--text-primary)" }}>{VALUE_BADGE(p.value)}{p.aiInitiative ? " 🤖" : ""} {p.name}</div>
                    </div>
                    {canEdit && (
                      <div style={{ display:"flex", gap:2, marginLeft:4, flexShrink:0 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onReorderBacklog?.(idx, 0); }}
                          disabled={idx === 0}
                          title="Move to top"
                          style={{ fontSize:10, lineHeight:1, padding:"1px 4px", border:"1px solid var(--border)", borderRadius:3,
                            background:"var(--bg-hover)", color: idx === 0 ? "var(--text-muted)" : "var(--text-secondary)",
                            cursor: idx === 0 ? "default" : "pointer", opacity: idx === 0 ? 0.4 : 1 }}>↟</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onReorderBacklog?.(idx, backlog.length); }}
                          disabled={idx === backlog.length - 1}
                          title="Move to bottom"
                          style={{ fontSize:10, lineHeight:1, padding:"1px 4px", border:"1px solid var(--border)", borderRadius:3,
                            background:"var(--bg-hover)", color: idx === backlog.length - 1 ? "var(--text-muted)" : "var(--text-secondary)",
                            cursor: idx === backlog.length - 1 ? "default" : "pointer", opacity: idx === backlog.length - 1 ? 0.4 : 1 }}>↡</button>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize:10, color:lane?.color, fontWeight:700, marginBottom:5 }}>{lane?.label}</div>
                  <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                    <DriverTag driver={p.driver} compact />
                    {p.keyCode && (
                      <span title={`Key Code: ${p.keyCode}`}
                        style={{ ...S.pillMuted, fontFamily:"ui-monospace,monospace", fontWeight:800, letterSpacing:"0.05em" }}>
                        {p.keyCode}
                      </span>
                    )}
                    <span style={S.pillMuted}>{p.value}</span>
                    <span style={{ ...S.pillMuted, background:"#EDE9FE", color:"#5B21B6", border:"1px solid #C4B5FD" }}>{p.sprints}sp</span>
                    {p.confluenceUrl && (
                      <a href={p.confluenceUrl} target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()} style={{ display:"inline-flex", alignItems:"center", textDecoration:"none" }} title="Business Case">
                        <ConfluenceIcon />
                      </a>
                    )}
                  </div>
                </div>
                {isReordering && dropIndex === idx + 1 && <DropLine />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── TimelineView ─────────────────────────────────────────────────────────────
export default function TimelineView({ projects, backlog, completed, roadmapFilter, backlogOpen, onToggleBacklog, onDropCell, onDropBacklog,
  onRemove, onEdit, onRestore, onReorderBacklog, dragging, setDragging,
  hoverCell, setHoverCell, visibleSprints, canEdit, lanes = [], onQuickUpdate, scoringMethod = "score" }) {

  const visList  = SPRINTS.slice(visibleSprints[0], visibleSprints[1] + 1);
  const trackMap = assignTracks(projects);
  const didDrag  = useRef(false);

  const scrollRef = useRef(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    const saved = localStorage.getItem("timeline_scroll_left");
    if (saved !== null) {
      scrollRef.current.scrollLeft = parseInt(saved, 10);
    } else {
      const today = new Date();
      const idx = SPRINTS.findIndex(s => today >= s.start && today <= s.end);
      const sprintIdx = idx !== -1 ? idx : Math.max(0, SPRINTS.findIndex(s => s.start > today));
      scrollRef.current.scrollLeft = Math.max(0, sprintIdx * CELL_W);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = (e) => {
    localStorage.setItem("timeline_scroll_left", String(e.currentTarget.scrollLeft));
  };

  const todayDate = new Date();
  const activeSprintIdx = (() => {
    const idx = SPRINTS.findIndex(s => todayDate >= s.start && todayDate <= s.end);
    if (idx !== -1) return idx;
    const next = SPRINTS.findIndex(s => s.start > todayDate);
    return next !== -1 ? next : SPRINTS.length - 1;
  })();

  const groupBy = (fn) => {
    const groups = []; let cur = null;
    visList.forEach((s, i) => {
      const key = fn(s, visibleSprints[0] + i);
      if (!cur || cur.label !== key) { cur = { label: key, count: 1 }; groups.push(cur); }
      else cur.count++;
    });
    return groups;
  };
  const halfGroups  = groupBy((s, idx) => getHalf(idx, SPRINTS[0]?.start.getFullYear()));
  const monthGroups = groupBy((s) => getMonthLabel(s));

  const startDrag = (e, project) => {
    e.dataTransfer.effectAllowed = "move";
    didDrag.current = false;
    setDragging({ project, source:"grid" });
  };
  const onDragMove = () => { didDrag.current = true; };

  const gridW = 140 + visList.length * CELL_W;

  return (
    <>
    <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>
      <div style={{ flex:1, overflow:"hidden", display:"flex", minHeight:0 }}>
      <div ref={scrollRef} onScroll={handleScroll} style={{ flex:1, overflowX:"auto", overflowY:"auto", minWidth:0 }}>
        <div style={{ width: gridW, display:"flex", flexDirection:"column" }}>

        {/* Header rows */}
        <div style={{ ...S.timelineRow, width: gridW }}>
          <div style={{ ...S.laneLabel, ...STICKY_LABEL, background:"var(--bg-subtle)" }} />
          <div style={{ display:"flex" }}>
            {halfGroups.map((g,i) => (
              <div key={i} style={{ ...S.halfHeader, width:g.count*CELL_W, background:i%2===0?"var(--accent-dark)":"var(--accent)" }}>{g.label}</div>
            ))}
          </div>
        </div>

        <div style={{ ...S.timelineRow, width: gridW }}>
          <div style={{ ...S.laneLabel, ...STICKY_LABEL }} />
          <div style={{ display:"flex" }}>
            {monthGroups.map((g,i) => (
              <div key={i} style={{ ...S.monthHeader, width:g.count*CELL_W, background:i%2===0?"var(--bg-subtle)":"var(--bg-hover)" }}>{g.label}</div>
            ))}
          </div>
        </div>

        <div style={{ ...S.timelineRow, width: gridW }}>
          <div style={{ ...S.laneLabel, ...STICKY_LABEL }} />
          <div style={{ display:"flex" }}>
            {visList.map((s,i) => {
              const abs = visibleSprints[0]+i;
              const isCur=abs===activeSprintIdx, isNext=abs===activeSprintIdx+1;
              return (
                <div key={s.id} style={{ ...S.sprintHeader, width:CELL_W,
                  background:isCur?"var(--accent)":isNext?"var(--sprint-next-bg)":"var(--bg-subtle)",
                  color:isCur?"#fff":isNext?"var(--text-primary)":"var(--text-secondary)", borderRight:"1px solid var(--border)" }}>
                  <span style={{ fontWeight:700, fontSize:11 }}>{s.label}</span>
                  <span style={{ fontSize:9, opacity:0.75 }}>{formatDate(s.start)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Lane rows */}
        {lanes.map((lane) => {
          const laneProjects = projects.filter((p) => {
            if (p.lane !== lane.id) return false;
            if (roadmapFilter === "ai") return p.aiInitiative;
            if (roadmapFilter === "revenue") return p.driver === "revenue";
            if (roadmapFilter === "core") return p.driver === "core";
            return true;
          });
          const numTracks    = tracksUsed(projects, lane.id, trackMap);
          const laneH        = laneHeight(numTracks);

          return (
            <div key={lane.id} style={{ display:"flex", alignItems:"stretch", borderBottom:"1px solid var(--border)", height:laneH, width: gridW, flexShrink:0, background:`${lane.color}0D` }}>
              <div style={{ ...S.laneLabel, ...STICKY_LABEL, borderLeft:`4px solid ${lane.color}`, background: "var(--bg-subtle)",
                flexDirection:"column", alignItems:"flex-start", justifyContent:"center", gap:4 }}>
                <span style={{ fontSize:11, fontWeight:800, color:lane.color, letterSpacing:"0.04em", textTransform:"uppercase" }}>{lane.label}</span>
                {numTracks > 1 && <span style={{ fontSize:9, color:lane.color+"99", fontWeight:600 }}>{numTracks} tracks</span>}
              </div>

              <div style={{ position:"relative", width: visList.length * CELL_W, flexShrink:0 }}>
                {Array.from({ length: numTracks - 1 }).map((_, ti) => (
                  <div key={ti} style={{ position:"absolute", left:0, right:0,
                    top: LANE_PAD + (ti + 1) * TRACK_H, height:1,
                    background:"rgba(0,0,0,0.06)", zIndex:1, pointerEvents:"none" }} />
                ))}

                {/* Current sprint column highlight */}
                {visList.map((_s, i) => {
                  const abs = visibleSprints[0] + i;
                  if (abs !== activeSprintIdx) return null;
                  return (
                    <div key="current-sprint-col" style={{
                      position:"absolute", left: i * CELL_W, top: 0,
                      width: CELL_W, height: laneH,
                      background: "var(--accent-glow)",
                      borderLeft: "2px solid var(--accent)",
                      pointerEvents: "none", zIndex: 1,
                    }} />
                  );
                })}

                {visList.map((s,i) => {
                  const abs = visibleSprints[0]+i;
                  return Array.from({ length: numTracks }).map((_, trackIdx) => {
                    const isHov = hoverCell?.lane===lane.id && hoverCell?.sprint===abs && hoverCell?.track===trackIdx;
                    return (
                      <div key={`${s.id}-t${trackIdx}`} style={{
                        position:"absolute", left:i*CELL_W, top:LANE_PAD+trackIdx*TRACK_H,
                        width:CELL_W, height:TRACK_H,
                        background:isHov?"var(--accent-glow)":"transparent",
                        borderRight:"1px solid var(--border)", boxSizing:"border-box",
                        transition:"background 0.12s", zIndex:2,
                      }}
                        onDragOver={(e) => { if (canEdit) { e.preventDefault(); setHoverCell({ lane:lane.id, sprint:abs, track:trackIdx }); } }}
                        onDragLeave={() => setHoverCell(null)}
                        onDrop={canEdit ? (e) => onDropCell(e, lane.id, abs, trackIdx) : undefined}
                      />
                    );
                  });
                })}

                {laneProjects.map((p) => {
                  const visStart = p.sprint - visibleSprints[0];
                  const visEnd   = visStart + p.sprints;
                  if (visEnd <= 0 || visStart >= visList.length) return null;
                  const cStart  = Math.max(0, visStart);
                  const cEnd    = Math.min(visList.length, visEnd);
                  const blockW  = (cEnd - cStart) * CELL_W - 18;
                  const track   = trackMap[p.id] ?? 0;
                  const topPos  = LANE_PAD + track * TRACK_H + TRACK_PAD;
                  const blockH  = 65;
                  const isNotCommitted = p.status === "notcommitted";
                  const laneColor = lane.color;

                  const cardBg = `${laneColor}0A`;
                  const cardBorder = p.atRisk ? "#EF4444" : `${laneColor}55`;
                  const cardOpacity = isNotCommitted ? 0.78 : 1;
                  const baseShadow = p.atRisk ? "0 0 0 2px #EF444430, 0 2px 6px rgba(0,0,0,0.10)" : "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)";
                  const hoverShadow = p.atRisk ? "0 0 0 2px #EF444470, 0 4px 12px rgba(0,0,0,0.14)" : `0 0 0 2px ${laneColor}70, 0 4px 10px rgba(0,0,0,0.10)`;

                  return (
                    <div key={p.id} draggable={canEdit}
                      onDragStart={canEdit ? (e) => startDrag(e, p) : undefined}
                      onDrag={canEdit ? onDragMove : undefined}
                      onClick={() => { if (!didDrag.current) onEdit?.(p); didDrag.current = false; }}
                      style={{
                        position:"absolute", zIndex:10, userSelect:"none", cursor: canEdit ? "pointer" : "default",
                        left:cStart*CELL_W + 2, top:topPos, width:blockW, height:blockH,
                        borderRadius:7, opacity: cardOpacity,
                        background: cardBg,
                        border:`1px ${isNotCommitted ? "dashed" : "solid"} ${cardBorder}`,
                        borderTop:`3px solid ${p.atRisk ? "#EF4444" : laneColor}`,
                        boxShadow: baseShadow,
                        padding:"4px 8px 5px",
                        display:"flex", flexDirection:"column", justifyContent:"space-between",
                        overflow:"hidden", transition:"box-shadow 0.15s, transform 0.1s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = hoverShadow; e.currentTarget.style.transform = "translateY(-1px)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = baseShadow; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.opacity = String(cardOpacity); }}
                      onMouseDown={(e) => { e.currentTarget.style.opacity = "0.82"; e.currentTarget.style.transform = "translateY(0) scale(0.99)"; }}
                      onMouseUp={(e)   => { e.currentTarget.style.opacity = String(cardOpacity); e.currentTarget.style.transform = "translateY(-1px)"; }}
                      title={p.name}
                      aria-label={`${p.name}${canEdit ? " — click to edit, drag to move" : ""}`}
                    >
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:3 }}>
                        <span style={{ fontSize:11, fontWeight:700, color:"var(--text-primary)", lineHeight:1.35, flex:1,
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", letterSpacing:"-0.01em" }}>
                          {p.atRisk && <span style={{ marginRight:2 }} title="At Risk">🚩</span>}
                          {p.aiInitiative && <span style={{ marginRight:2 }} title="AI Initiative">🤖</span>}
                          {p.blockedBy?.length > 0 && <span style={{ marginRight:2, fontSize:10 }} title={`Blocked by ${p.blockedBy.length} project${p.blockedBy.length!==1?"s":""}`}>⛓</span>}
                          {p.status === "inprogress" && <span style={{ marginRight:2, color:"var(--accent)" }} title="In Progress">▶</span>}
                          {p.status === "notcommitted" && <span style={{ marginRight:2, color:"#B45309", fontWeight:800 }} title="Not Committed">?</span>}
                          {p.name}
                        </span>
                        {p.imageUrl && (
                          <img src={p.imageUrl} alt="" style={{ width:18, height:18, borderRadius:4, objectFit:"cover", flexShrink:0, cursor:"pointer", marginTop:1 }}
                            onClick={(e) => { e.stopPropagation(); window.open(p.imageUrl, "_blank"); }}
                            onError={(e) => { e.target.style.display = "none"; }} />
                        )}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                        <div style={{ display:"flex", gap:2, flexWrap:"nowrap", alignItems:"center", overflow:"hidden" }}>
                          <DriverTag driver={p.driver} compact />
                          {p.keyCode && (
                            <span
                              title={`Key Code: ${p.keyCode}`}
                              style={{ ...S.pillMuted, fontFamily:"ui-monospace,monospace", fontWeight:800, letterSpacing:"0.05em", flexShrink:0 }}
                            >{p.keyCode}</span>
                          )}
                          {p.value && <span title={`Revenue: ${p.value}`} style={{ ...S.pill, background:"#DCFCE7", color:"#15803D", border:"1px solid #86EFAC", flexShrink:0 }}>$</span>}
                          {p.notes && <NotesDot notes={p.notes} />}
                        </div>
                        <div style={{ display:"flex", gap:6, overflow:"hidden", lineHeight:1, alignItems:"center" }}>
                          {p.jiraStatus && <JiraStatusBadge status={p.jiraStatus} statusCategory={p.jiraStatusCategory} />}
                          {p.pmName  && <span style={{ fontSize:9, color:"var(--text-muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={`Product Manager: ${p.pmName}`}><span style={{ fontWeight:700, color:"var(--text-secondary)" }}>PM</span> {p.pmName}</span>}
                          {p.engLead && <span style={{ fontSize:9, color:"var(--text-muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={`Engineering Lead: ${p.engLead}`}><span style={{ fontWeight:700, color:"var(--text-secondary)" }}>EL</span> {p.engLead}</span>}
                          {p.poName  && <span style={{ fontSize:9, color:"var(--text-muted)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={`Product Owner: ${p.poName}`}><span style={{ fontWeight:700, color:"var(--text-secondary)" }}>PO</span> {p.poName}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Unassigned row — catches projects whose lane ID no longer exists */}
        {(() => {
          const knownIds   = new Set(lanes.map(l => l.id));
          const unassigned = projects.filter(p => p.sprint !== undefined && !knownIds.has(p.lane));
          if (unassigned.length === 0) return null;
          const numTracks = Math.max(1, ...unassigned.map(p => (trackMap[p.id] ?? 0) + 1));
          const laneH     = laneHeight(numTracks);
          return (
            <div style={{ display:"flex", alignItems:"stretch", borderBottom:"1px solid #FED7AA",
              height:laneH, width:gridW, flexShrink:0, background:"#FFFBEB" }}>
              <div style={{ ...S.laneLabel, ...STICKY_LABEL, borderLeft:`4px solid #F59E0B`, background:"#FEF3C7",
                flexDirection:"column", alignItems:"flex-start", justifyContent:"center", gap:4 }}>
                <span style={{ fontSize:11, fontWeight:800, color:"#B45309", letterSpacing:"0.04em", textTransform:"uppercase" }}>Unassigned</span>
                <span style={{ fontSize:9, color:"#D97706", fontWeight:600 }}>Edit to reassign team</span>
              </div>
              <div style={{ position:"relative", width:visList.length * CELL_W, flexShrink:0 }}>
                {visList.map((_s,i) => (
                  <div key={i} style={{ position:"absolute", left:i*CELL_W, top:0, width:CELL_W, height:laneH,
                    background:"transparent", borderRight:"1px solid #FED7AA", pointerEvents:"none" }} />
                ))}
                {unassigned.map((p) => {
                  const visStart = p.sprint - visibleSprints[0];
                  const visEnd   = visStart + p.sprints;
                  if (visEnd <= 0 || visStart >= visList.length) return null;
                  const cStart = Math.max(0, visStart);
                  const cEnd   = Math.min(visList.length, visEnd);
                  const blockW = (cEnd - cStart) * CELL_W - 20;
                  const track  = trackMap[p.id] ?? 0;
                  const topPos = LANE_PAD + track * TRACK_H + TRACK_PAD;
                  return (
                    <div key={p.id}
                      onClick={() => onEdit?.(p)}
                      style={{
                        position:"absolute", zIndex:10, cursor:canEdit ? "pointer" : "default",
                        left:cStart*CELL_W, top:topPos, width:blockW, height:46,
                        borderRadius:5, background:"#FEF3C7",
                        border:"1px dashed #FCD34D", borderTop:`3px solid #F59E0B`,
                        boxShadow:"0 1px 4px rgba(0,0,0,0.08)",
                        padding:"4px 7px", display:"flex", flexDirection:"column", justifyContent:"space-between",
                        overflow:"hidden",
                      }}
                      title="Team no longer exists — click to reassign"
                    >
                      <div style={{ fontSize:10, fontWeight:700, color:"#92400E", lineHeight:1.3,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>⚠ {p.name}</div>
                      <div style={{ fontSize:9, color:"#B45309" }}>Unknown team · click to reassign</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Capacity row */}
        <div style={{ ...S.capRow, width: gridW }}>
          <div style={{ ...S.laneLabel, ...STICKY_LABEL, background:"var(--bg-subtle)" }}>
            <span style={{ fontSize:10, color:"var(--text-muted)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em" }}>Capacity</span>
          </div>
          <div style={{ display:"flex" }}>
            {visList.map((s,i) => {
              const abs             = visibleSprints[0]+i;
              const tracksUsedTotal = lanes.reduce((sum,l) => sum + getTracksUsedInSprint(projects, l.id, abs, trackMap), 0);
              const normalMax       = lanes.reduce((sum,l) => sum + l.capacity, 0);
              const pct             = Math.round((tracksUsedTotal / normalMax) * 100);
              const capLabel = pct > 100 ? "Over" : pct >= 80 ? "Full" : "Under";
              const capColor = pct > 100 ? "#EF4444" : pct >= 80 ? "#16A34A" : "#84CC16";
              return (
                <div key={s.id} style={{ ...S.capCell, width:CELL_W, borderRight:"1px solid #E2E8F0" }}>
                  <span style={{ fontSize:10, fontWeight:800, color:capColor }}>{capLabel}</span>
                </div>
              );
            })}
          </div>
        </div>

        <CompletedSection
          completed={completed}
          visibleSprints={visibleSprints}
          onEdit={onEdit}
          onRestore={onRestore}
          lanes={lanes}
          scoringMethod={scoringMethod}
        />

        </div>
      </div>

      <BacklogPanel
        backlog={backlog}
        dragging={dragging}
        setDragging={setDragging}
        onDropBacklog={onDropBacklog}
        onReorderBacklog={onReorderBacklog}
        onEdit={onEdit}
        onRemove={onRemove}
        open={backlogOpen}
        onToggle={onToggleBacklog}
        canEdit={canEdit}
        lanes={lanes}
        scoringMethod={scoringMethod}
      />
      </div>

      {/* Legend */}
      <div style={S.legend}>
        <span style={S.legendSect}>Driver</span>
        {Object.entries(DRIVERS).map(([k,d]) => (
          <span key={k} style={S.legendItem}>
            <span style={{ width:8,height:8,borderRadius:"50%",background:d.color,display:"inline-block" }} />{d.label}
          </span>
        ))}
        <span style={{ ...S.legendSect, marginLeft:16 }}>Score</span>
        <span style={S.legendItem}><span style={{ ...S.dot,background:"#F59E0B" }} />High (80+)</span>
        <span style={S.legendItem}><span style={{ ...S.dot,background:"#6EE7B7" }} />Mid (50–79)</span>
        <span style={S.legendItem}><span style={{ ...S.dot,background:"#CBD5E1" }} />Low (&lt;50)</span>
        <span style={{ ...S.legendItem,color:"#0369A1",marginLeft:16 }}>✓ Completed</span>
        <span style={{ ...S.legendItem,color:"#7C3AED",marginLeft:8 }}>▶ In Progress</span>
        <span style={{ ...S.legendItem,color:"#B45309",marginLeft:8 }}>? Not Committed</span>
        <span style={{ ...S.legendItem,color:"#EF4444",marginLeft:8 }}>🚩 At Risk</span>
        <span style={{ ...S.legendItem,marginLeft:8 }}>🤖 AI Initiative</span>
      </div>
    </div>
    </>
  );
}
